import { useCallback, useImperativeHandle, useState } from 'react';

import { checkSleepHours, checkWeight, readDecimal } from '@psy/validations';

import { useLanguage } from '../../contexts/language';
import { useField, validateAll } from '../../hooks/field';
import { useRecordChanges } from '../../hooks/record-changes';
import { useVisit } from '../../hooks/visit';

import { PageTitle } from '../page-title';
import { StepActions } from '../step-actions';
import { TextField } from '../text-field';

import styles from './style.module.css';

// The value to send for one field: nothing when it was empty and still is,
// null when an earlier answer was emptied (which clears it), else the number
// as the shared rules read it, comma included.
function change({ value, saved }) {
  if (value.trim() === '' && saved == null) {
    return undefined;
  }

  if (value.trim() === '') {
    return null;
  }

  return readDecimal(value);
}

// Step 4: hours slept, and the weight, which is always there to fill in
// though the clinician asks for it on the 28th. Both may stay empty. The
// sleep period is the one agreed with the clinician (O09); it is not decided
// here.
export function SleepStep({ ref, record, onBack, onSkip, onMove, onSaving, onStopped, onDone }) {
  const { text, language, translate } = useLanguage();
  const { save } = useRecordChanges();
  const capture = useVisit();
  const [ busy, setBusy ] = useState(false);
  const number = new Intl.NumberFormat(language, { maximumFractionDigits: 1, useGrouping: false });

  // A saved answer is shown in the device's own format, an empty one as an
  // empty field.
  const shown = function(value) {
    if (value == null) {
      return '';
    }

    return number.format(value);
  };

  const sleep = useField({ initial: shown(record.sleepHours), check: checkSleepHours });
  const weight = useField({ initial: shown(record.weightKg), check: checkWeight });

  // Saves what was chosen, then goes to `to`, or to the next step when to is
  // null, as Next does; the bar calls it with the step it chose (#158).
  const submit = useCallback(function(to) {
    if (validateAll([ sleep, weight ]) === false) {
      return null;
    }

    const input = {};
    const sleepHours = change({ value: sleep.value, saved: record.sleepHours });
    const weightKg = change({ value: weight.value, saved: record.weightKg });

    if (sleepHours !== undefined) {
      input.sleepHours = sleepHours;
    }

    if (weightKg !== undefined) {
      input.weightKg = weightKg;
    }

    if (Object.keys(input).length === 0) {
      return onMove(to);
    }

    setBusy(true);

    const move = onSaving(to);
    const owns = capture();

    return save({ date: record.date, input }).then(function() {
      if (owns() === false) {
        return onStopped(move);
      }

      return onDone(move);
    }).catch(function(failed) {
      onStopped(move);

      if (owns() === false) {
        return;
      }

      setBusy(false);
      sleep.refuse(failed.message);
    });
  }, [ sleep, weight, record, save, onMove, onSaving, onStopped, onDone, capture ]);

  const onSubmit = useCallback(function(event) {
    event.preventDefault();
    submit(null);
  }, [ submit ]);

  useImperativeHandle(ref, function() {
    return { submit };
  }, [ submit ]);

  return (
    <form className={ styles.form } onSubmit={ onSubmit } noValidate>
      <PageTitle title={ text.sleepQuestion }/>

      <TextField
        ref={ sleep.ref }
        id='sleep-hours'
        label={ text.hoursSlept }
        value={ sleep.value }
        onChange={ sleep.onChange }
        onBlur={ sleep.onBlur }
        error={ translate(sleep.error) }
        hint={ text.sleepHint }
        inputMode='decimal'
      />

      <TextField
        ref={ weight.ref }
        id='weight'
        label={ `${ text.weight } (${ text.kg })` }
        value={ weight.value }
        onChange={ weight.onChange }
        onBlur={ weight.onBlur }
        error={ translate(weight.error) }
        hint={ text.weightHint }
        inputMode='decimal'
      />

      <StepActions onBack={ onBack } submitLabel={ text.next } busy={ busy } skipLabel={ text.skip } onSkip={ onSkip }/>
    </form>
  );
}
